import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { addRecentSearch, formatPrice, getEffectivePrice, getProductUrl, resolveImageUrl } from '../../utils/shopHelpers';
import './ShopHeaderSearch.css';

const MIN_CHARS = 3;
const DEBOUNCE_MS = 280;

function ShopHeaderSearch({ className = '', inputClassName = '', buttonClassName = '' }) {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const debounceRef = useRef(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [activeSuggestion, setActiveSuggestion] = useState(0);

  const trimmed = query.trim();
  const showPanel = open && trimmed.length >= MIN_CHARS;

  const closePanel = useCallback(() => {
    setOpen(false);
  }, []);

  const goToSearch = useCallback((term) => {
    const q = String(term || '').trim();
    if (!q) return;
    addRecentSearch(q);
    closePanel();
    navigate(`/shop/search?q=${encodeURIComponent(q)}`);
  }, [closePanel, navigate]);

  const fetchSuggestions = useCallback(async (term) => {
    try {
      setLoading(true);
      const res = await api.searchPublicProductsSuggest(term);
      const payload = res?.data || {};
      setSuggestions(Array.isArray(payload.suggestions) ? payload.suggestions : []);
      setProducts(Array.isArray(payload.products) ? payload.products : []);
      setTotal(Number(payload.total) || 0);
      setActiveSuggestion(0);
    } catch {
      setSuggestions([term]);
      setProducts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (trimmed.length < MIN_CHARS) {
      setSuggestions([]);
      setProducts([]);
      setTotal(0);
      setLoading(false);
      return undefined;
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(trimmed);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [trimmed, fetchSuggestions]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        closePanel();
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closePanel();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [closePanel]);

  const handleSubmit = (e) => {
    e.preventDefault();
    goToSearch(trimmed);
  };

  const handleSuggestionClick = (term) => {
    setQuery(term);
    goToSearch(term);
  };

  const handleInputKeyDown = (e) => {
    if (!showPanel || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestion((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestion((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Tab' && suggestions[activeSuggestion]) {
      e.preventDefault();
      setQuery(suggestions[activeSuggestion]);
    }
  };

  const panelSuggestions = suggestions.length ? suggestions : [trimmed];

  return (
    <div className={`shop-header-search ${className}`.trim()} ref={rootRef}>
      {showPanel && <div className="shop-header-search__backdrop" aria-hidden onClick={closePanel} />}
      <form className="shop-header-search__form" onSubmit={handleSubmit}>
        <input
          type="search"
          placeholder="Search keyword, SKU or part #"
          className={`shop-header-search__input ${inputClassName}`.trim()}
          value={query}
          autoComplete="off"
          aria-expanded={showPanel}
          aria-controls="shop-search-mega-panel"
          aria-autocomplete="list"
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={handleInputKeyDown}
        />
        <button type="submit" className={`shop-header-search__btn ${buttonClassName}`.trim()} aria-label="Search">
          🔍
        </button>
      </form>

      {showPanel && (
        <div id="shop-search-mega-panel" className="shop-header-search__panel" role="listbox">
          <div className="shop-header-search__panel-inner">
            <aside className="shop-header-search__suggestions">
              {panelSuggestions.map((term, index) => (
                <button
                  key={term}
                  type="button"
                  role="option"
                  aria-selected={index === activeSuggestion}
                  className={`shop-header-search__suggestion${index === activeSuggestion ? ' is-active' : ''}${index === 0 ? ' is-primary' : ''}`}
                  onMouseEnter={() => setActiveSuggestion(index)}
                  onClick={() => handleSuggestionClick(term)}
                >
                  {term}
                </button>
              ))}
            </aside>

            <div className="shop-header-search__results">
              {loading && (
                <p className="shop-header-search__status">Searching…</p>
              )}
              {!loading && products.length === 0 && (
                <p className="shop-header-search__status">No matching products yet. Press Enter to see all results.</p>
              )}
              {!loading && products.length > 0 && (
                <div className="shop-header-search__product-grid">
                  {products.map((product) => {
                    const price = getEffectivePrice(product);
                    const image = resolveImageUrl(product.primary_image_url, product.sku || product.name);
                    const detailUrl = getProductUrl(product);
                    return (
                      <Link
                        key={product.id}
                        to={detailUrl}
                        className="shop-header-search__product"
                        onClick={closePanel}
                      >
                        <img
                          src={image}
                          alt=""
                          className="shop-header-search__product-image"
                          loading="lazy"
                          onError={(e) => {
                            e.target.src = `https://via.placeholder.com/120x120?text=${encodeURIComponent(product.sku || 'SKU')}`;
                          }}
                        />
                        <div className="shop-header-search__product-body">
                          <strong>{product.name}</strong>
                          {product.sku && <span className="shop-header-search__product-sku">({product.sku})</span>}
                          <span className="shop-header-search__product-price">{formatPrice(price)}</span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <button
            type="button"
            className="shop-header-search__see-all"
            onClick={() => goToSearch(trimmed)}
          >
            See {total || 'all'} result{(total !== 1) ? 's' : ''} for &apos;{trimmed}&apos;
            <span aria-hidden>›</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default ShopHeaderSearch;
